
/**
 * Trade-aware live preview that updates as the customer makes selections.
 * Renders a different visualization for each trade (decks, concrete, painting, roofing).
 * Background adapts to the market's geographic region.
 * Uses CSS 3D transforms — no external dependencies.
 */

// ─── DECK MATERIAL COLORS ───
const MATERIAL_COLORS = {
  "trex-enhance":      { deck: "#b39573", board: "rgba(0,0,0,0.08)", label: "Trex Enhance" },
  "trex-select":       { deck: "#9e8367", board: "rgba(0,0,0,0.1)",  label: "Trex Select" },
  "trex-transcend":    { deck: "#7a5c3a", board: "rgba(0,0,0,0.1)",  label: "Trex Transcend" },
  "redwood":           { deck: "#8b4513", board: "rgba(0,0,0,0.12)", label: "Redwood" },
  "cedar-decking":     { deck: "#c49a6c", board: "rgba(0,0,0,0.08)", label: "Cedar" },
  "pressure-treated":  { deck: "#8fbc8f", board: "rgba(0,0,0,0.1)",  label: "Pressure-Treated" },
  "composite-generic": { deck: "#a08868", board: "rgba(0,0,0,0.08)", label: "Composite" },
  "ipe":               { deck: "#5c3a1e", board: "rgba(255,255,255,0.05)", label: "Ipê" },
  "timbertech":        { deck: "#6b4a30", board: "rgba(0,0,0,0.1)",  label: "TimberTech AZEK" },
};

const RAILING_STYLES = {
  cable:     { color: "rgba(180,180,180,0.6)", width: 1, lines: 4 },
  glass:     { color: "rgba(200,220,240,0.3)", width: 0, lines: 0, panel: true },
  wood:      { color: "#b39573", width: 2, lines: 0, balusters: true },
  composite: { color: "#a0826d", width: 2, lines: 0, balusters: true },
  metal:     { color: "#888", width: 1.5, lines: 0, balusters: true },
  none:      null,
};

// ─── REGION FROM MARKET STATE ───
function getRegion(state) {
  if (["CO", "ID", "WY"].includes(state)) return "mountain";
  if (["NY", "NH", "ME", "DE"].includes(state)) return "northeast";
  if (["OK", "TX", "KY"].includes(state)) return "south";
  if (["SD"].includes(state)) return "plains";
  // OH, IN, IA, IL, MO, NE, KS + default
  return "midwest";
}

// ─── REGION BACKGROUNDS ───
const REGION_BACKGROUNDS = {
  mountain: {
    sky: "linear-gradient(180deg, #4a88c8 0%, #87CEEB 35%, #5a8f5a 60%, #4a7c49 70%, #6b8a60 100%)",
    hasMountains: true,
    hasSnow: true,
    treeColor: "rgba(40,80,40,0.5)",
    groundColor: "#5a8f5a",
  },
  midwest: {
    sky: "linear-gradient(180deg, #6aaddf 0%, #a8d8f0 30%, #7ab870 55%, #5a9a50 70%, #7ab060 100%)",
    hasMountains: false,
    hasSnow: false,
    treeColor: "rgba(50,100,50,0.45)",
    groundColor: "#6aab5a",
  },
  northeast: {
    sky: "linear-gradient(180deg, #5a8ab8 0%, #90b8d8 30%, #c8a060 55%, #7a6040 70%, #8a7850 100%)",
    hasMountains: false,
    hasSnow: false,
    treeColor: "rgba(160,80,20,0.5)",
    groundColor: "#8a7040",
  },
  south: {
    sky: "linear-gradient(180deg, #6090c8 0%, #a0c8e8 30%, #c8a870 55%, #9a8050 70%, #a89060 100%)",
    hasMountains: false,
    hasSnow: false,
    treeColor: "rgba(40,90,40,0.4)",
    groundColor: "#9a8a50",
  },
  plains: {
    sky: "linear-gradient(180deg, #5080c0 0%, #88c0e8 40%, #d8c890 65%, #a89860 80%, #b0a070 100%)",
    hasMountains: false,
    hasSnow: false,
    treeColor: "rgba(50,80,30,0.35)",
    groundColor: "#a89860",
  },
};

// ─── BACKGROUND SCENE ───
function BackgroundScene({ region, hasLighting }) {
  const bg = REGION_BACKGROUNDS[region] || REGION_BACKGROUNDS.midwest;

  return (
    <>
      {/* Sky + ground gradient */}
      <div style={{
        position: "absolute", inset: 0,
        background: bg.sky,
      }} />

      {/* Sun */}
      <div style={{
        position: "absolute", top: 14, right: 20,
        width: 20, height: 20, borderRadius: "50%",
        background: hasLighting ? "rgba(255,200,50,0.9)" : "rgba(255,240,200,0.85)",
        boxShadow: hasLighting ? "0 0 22px rgba(255,200,50,0.5)" : "0 0 16px rgba(255,240,200,0.45)",
      }} />

      {/* Mountains (mountain region only) */}
      {bg.hasMountains && (
        <svg style={{ position: "absolute", top: 22, left: 0, width: "100%", height: 80 }} viewBox="0 0 600 80" preserveAspectRatio="none">
          <polygon points="0,80 60,15 140,55 220,5 340,45 460,10 560,50 600,30 600,80" fill="rgba(90,120,90,0.35)" />
          <polygon points="0,80 100,35 220,65 350,20 480,55 600,40 600,80" fill="rgba(70,100,70,0.25)" />
          {bg.hasSnow && (
            <>
              <polygon points="215,5 232,20 203,20" fill="rgba(255,255,255,0.65)" />
              <polygon points="456,10 474,26 442,26" fill="rgba(255,255,255,0.55)" />
            </>
          )}
        </svg>
      )}

      {/* Rolling hills (non-mountain) */}
      {!bg.hasMountains && (
        <svg style={{ position: "absolute", top: 28, left: 0, width: "100%", height: 60 }} viewBox="0 0 600 60" preserveAspectRatio="none">
          <path d="M0,60 Q150,20 300,40 Q450,60 600,30 L600,60 Z" fill="rgba(80,120,60,0.2)" />
          <path d="M0,60 Q200,35 400,50 Q500,56 600,42 L600,60 Z" fill="rgba(80,120,60,0.15)" />
        </svg>
      )}

      {/* Trees */}
      {[38, 112, 476, 548].map((x, i) => (
        <div key={`tree-${i}`} style={{ position: "absolute", bottom: "44%", left: x }}>
          <div style={{ width: 0, height: 0, borderLeft: "10px solid transparent", borderRight: "10px solid transparent", borderBottom: `30px solid ${bg.treeColor}`, position: "relative", left: -10 }} />
          <div style={{ width: 3, height: 8, background: "rgba(80,50,30,0.4)", margin: "0 auto", position: "relative", left: -1 }} />
        </div>
      ))}
    </>
  );
}

// ─── DECK PREVIEW ───
function DeckScene({ sqft, material, railing, extras, color, region }) {
  const width = Math.round(Math.sqrt(sqft || 400) * 1.25);
  const depth = Math.round(Math.sqrt(sqft || 400) * 0.8);
  const defaultMat = MATERIAL_COLORS[material?.id] || MATERIAL_COLORS["trex-enhance"];
  const matStyle = color?.hex ? { ...defaultMat, deck: color.hex } : defaultMat;
  const railStyle = railing?.id ? (RAILING_STYLES[railing.id] || null) : null;
  const hasStairs   = extras?.some(e => e.id === "stairs");
  const hasPergola  = extras?.some(e => e.id === "pergola");
  const hasLighting = extras?.some(e => e.id === "lighting");
  const hasBenches  = extras?.some(e => e.id === "benches");
  const hasHotTub   = extras?.some(e => e.id === "hottub");
  const hasFirePit  = extras?.some(e => e.id === "firepit");

  return (
    <>
      <BackgroundScene region={region} hasLighting={hasLighting} />

      {/* Deck platform */}
      <div style={{
        position: "absolute", bottom: "12%", left: "12%", right: "12%", height: "30%",
        background: matStyle.deck,
        borderRadius: "3px 3px 0 0",
        transform: "perspective(500px) rotateX(22deg)",
        transformOrigin: "bottom center",
        boxShadow: "0 -3px 15px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)",
        transition: "background 0.5s ease",
      }}>
        {/* Deck boards */}
        {Array.from({ length: 14 }).map((_, i) => (
          <div key={`board-${i}`} style={{ position: "absolute", top: `${i * 7.14}%`, left: 0, right: 0, height: "1px", background: matStyle.board }} />
        ))}

        {hasHotTub && (
          <div style={{ position: "absolute", top: "15%", right: "8%", width: "22%", height: "55%", borderRadius: "50%", background: "rgba(100,160,200,0.4)", border: "2px solid rgba(80,140,180,0.5)" }} />
        )}
        {hasFirePit && (
          <div style={{ position: "absolute", top: "20%", left: "35%", width: "14%", height: "45%", borderRadius: "50%", background: "rgba(60,60,60,0.4)", border: "2px solid rgba(80,80,80,0.3)", boxShadow: hasLighting ? "0 0 12px rgba(255,120,20,0.4)" : "none" }} />
        )}
        {hasBenches && (
          <>
            <div style={{ position: "absolute", bottom: "8%", left: "5%", width: "25%", height: "8%", background: "rgba(0,0,0,0.15)", borderRadius: 2 }} />
            <div style={{ position: "absolute", bottom: "8%", right: "5%", width: "25%", height: "8%", background: "rgba(0,0,0,0.15)", borderRadius: 2 }} />
          </>
        )}
        {hasLighting && Array.from({ length: 6 }).map((_, i) => (
          <div key={`light-${i}`} style={{ position: "absolute", top: -3, left: `${10 + i * 16}%`, width: 5, height: 5, borderRadius: "50%", background: "rgba(255,220,100,0.9)", boxShadow: "0 0 8px rgba(255,220,100,0.6)" }} />
        ))}
      </div>

      {/* Railing */}
      {railStyle && (
        <>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={`post-${i}`} style={{ position: "absolute", bottom: "calc(12% + 30% - 3px)", left: `${12 + i * 15.2}%`, width: 3, height: 28, background: railStyle.balusters ? railStyle.color : "rgba(100,100,100,0.6)", transition: "background 0.5s" }} />
          ))}
          {railStyle.lines > 0 && Array.from({ length: railStyle.lines }).map((_, i) => (
            <div key={`cable-${i}`} style={{ position: "absolute", bottom: `calc(12% + 30% + ${6 + i * 6}px)`, left: "12%", right: "12%", height: railStyle.width, background: railStyle.color }} />
          ))}
          {railStyle.panel && (
            <div style={{ position: "absolute", bottom: "calc(12% + 30% - 3px)", left: "12%", right: "12%", height: 28, background: railStyle.color, borderTop: "1px solid rgba(200,220,240,0.4)" }} />
          )}
          {railStyle.balusters && Array.from({ length: 20 }).map((_, i) => (
            <div key={`bal-${i}`} style={{ position: "absolute", bottom: "calc(12% + 30% - 3px)", left: `${13 + i * 3.7}%`, width: 1.5, height: 24, background: railStyle.color, opacity: 0.5 }} />
          ))}
        </>
      )}

      {/* Pergola */}
      {hasPergola && (
        <>
          {[18, 82].map((x, i) => (
            <div key={`perg-post-${i}`} style={{ position: "absolute", bottom: "calc(12% + 30% - 3px)", left: `${x}%`, width: 4, height: 50, background: "rgba(139,93,60,0.5)" }} />
          ))}
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={`perg-beam-${i}`} style={{ position: "absolute", bottom: "calc(12% + 30% + 44px)", left: "16%", right: "16%", height: 3, background: "rgba(139,93,60,0.3)", transform: `translateY(${i * -7}px)` }} />
          ))}
        </>
      )}

      {/* Stairs */}
      {hasStairs && (
        <div style={{ position: "absolute", bottom: "6%", left: "40%", width: "20%" }}>
          {[0, 1, 2].map(i => (
            <div key={`stair-${i}`} style={{ width: `${100 - i * 10}%`, height: 6, margin: "0 auto 2px", background: matStyle.deck, opacity: 0.7 + i * 0.1, borderRadius: 1 }} />
          ))}
        </div>
      )}

      {/* Labels */}
      <div style={{ position: "absolute", bottom: 8, left: 12, padding: "4px 10px", borderRadius: 4, background: "rgba(0,0,0,0.55)", color: "white", fontSize: 10, fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>
        {matStyle.label}{color?.name ? ` · ${color.name}` : ""} · {width}' × {depth}'
      </div>
      <div style={{ position: "absolute", bottom: 8, right: 12, padding: "4px 10px", borderRadius: 4, background: "rgba(0,0,0,0.55)", color: "white", fontSize: 10, fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>
        {sqft || 400} sqft
      </div>
    </>
  );
}

// ─── CONCRETE PREVIEW ───
function ConcreteScene({ sqft, material, extras, color, region }) {
  const finish = material || { id: "broom", name: "Broom Finish", swatch: "#c8beb4" };
  const surfaceColor = color?.hex || finish.swatch || "#c8beb4";
  const isStamped = finish.id === "stamped";
  const isExposed = finish.id === "exposed-aggregate";
  const isPolished = finish.id === "polished";
  const hasSealer  = extras?.some(e => e.id === "sealer");
  const hasBorders = extras?.some(e => e.id === "borders");
  const hasDrainage = extras?.some(e => e.id === "drainage");
  const hasSteps   = extras?.some(e => e.id === "steps");
  const width = Math.round(Math.sqrt(sqft || 500) * 1.3);
  const depth = Math.round(Math.sqrt(sqft || 500) * 0.75);

  return (
    <>
      <BackgroundScene region={region} hasLighting={false} />

      {/* Concrete slab */}
      <div style={{
        position: "absolute", bottom: "14%", left: "10%", right: "10%", height: "28%",
        background: hasSealer
          ? `linear-gradient(135deg, ${surfaceColor}ee, ${surfaceColor}aa)`
          : surfaceColor,
        borderRadius: "2px 2px 0 0",
        transform: "perspective(500px) rotateX(20deg)",
        transformOrigin: "bottom center",
        boxShadow: "0 -2px 12px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.15)",
        transition: "background 0.5s ease",
        overflow: "hidden",
      }}>
        {/* Broom finish lines */}
        {!isStamped && !isExposed && !isPolished && Array.from({ length: 18 }).map((_, i) => (
          <div key={`broom-${i}`} style={{ position: "absolute", top: `${i * 5.6}%`, left: 0, right: 0, height: "1px", background: "rgba(0,0,0,0.07)" }} />
        ))}

        {/* Stamped pattern — grid of squares */}
        {isStamped && Array.from({ length: 4 }).map((_, row) =>
          Array.from({ length: 6 }).map((_, col) => (
            <div key={`stamp-${row}-${col}`} style={{
              position: "absolute",
              top: `${8 + row * 22}%`, left: `${3 + col * 16}%`,
              width: "14%", height: "18%",
              border: "1px solid rgba(0,0,0,0.12)",
              borderRadius: 1,
            }} />
          ))
        )}

        {/* Exposed aggregate — dots */}
        {isExposed && Array.from({ length: 40 }).map((_, i) => (
          <div key={`agg-${i}`} style={{
            position: "absolute",
            top: `${5 + (i * 17) % 85}%`,
            left: `${2 + (i * 23) % 90}%`,
            width: 3, height: 3, borderRadius: "50%",
            background: "rgba(100,80,60,0.3)",
          }} />
        ))}

        {/* Polished sheen */}
        {isPolished && (
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, rgba(255,255,255,0.18) 0%, transparent 60%)" }} />
        )}

        {/* Control joints */}
        <div style={{ position: "absolute", top: 0, bottom: 0, left: "33%", width: 1, background: "rgba(0,0,0,0.1)" }} />
        <div style={{ position: "absolute", top: 0, bottom: 0, left: "66%", width: 1, background: "rgba(0,0,0,0.1)" }} />
        <div style={{ position: "absolute", left: 0, right: 0, top: "50%", height: 1, background: "rgba(0,0,0,0.1)" }} />

        {/* Decorative border */}
        {hasBorders && (
          <>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 6, background: "rgba(0,0,0,0.12)", borderBottom: "1px solid rgba(0,0,0,0.08)" }} />
            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 6, background: "rgba(0,0,0,0.12)" }} />
            <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: 6, background: "rgba(0,0,0,0.1)" }} />
            <div style={{ position: "absolute", top: 0, bottom: 0, right: 0, width: 6, background: "rgba(0,0,0,0.1)" }} />
          </>
        )}

        {/* Drain channel */}
        {hasDrainage && (
          <div style={{ position: "absolute", bottom: "10%", left: "20%", right: "20%", height: 4, background: "rgba(0,0,0,0.2)", borderRadius: 2, boxShadow: "inset 0 1px 2px rgba(0,0,0,0.2)" }} />
        )}
      </div>

      {/* Steps */}
      {hasSteps && (
        <div style={{ position: "absolute", bottom: "8%", left: "38%", width: "24%" }}>
          {[0, 1, 2].map(i => (
            <div key={`step-${i}`} style={{ width: `${100 - i * 8}%`, height: 7, margin: "0 auto 2px", background: surfaceColor, opacity: 0.75 + i * 0.08, borderRadius: 1, boxShadow: "0 1px 2px rgba(0,0,0,0.15)" }} />
          ))}
        </div>
      )}

      {/* Labels */}
      <div style={{ position: "absolute", bottom: 8, left: 12, padding: "4px 10px", borderRadius: 4, background: "rgba(0,0,0,0.55)", color: "white", fontSize: 10, fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>
        {finish.name}{color?.name ? ` · ${color.name}` : ""} · {width}' × {depth}'
      </div>
      <div style={{ position: "absolute", bottom: 8, right: 12, padding: "4px 10px", borderRadius: 4, background: "rgba(0,0,0,0.55)", color: "white", fontSize: 10, fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>
        {sqft || 500} sqft
      </div>
    </>
  );
}

// ─── PAINTING PREVIEW ───
function PaintingScene({ sqft, material, extras, color, region }) {
  const service = material || { id: "exterior-full", name: "Exterior — Full House", swatch: "#e8d8c0" };
  const wallColor = color?.hex || service.swatch || "#e8d8c0";
  const isInterior = service.id?.startsWith("interior") || service.id === "cabinet-refinish";
  const isDeckStain = service.id === "deck-stain";
  const hasTrim    = extras?.some(e => e.id === "trim-windows");
  const hasGarage  = extras?.some(e => e.id === "garage-door");
  const hasFence   = extras?.some(e => e.id === "fence");

  return (
    <>
      <BackgroundScene region={region} hasLighting={false} />

      {isInterior ? (
        /* Interior room view */
        <div style={{
          position: "absolute", bottom: "10%", left: "15%", right: "15%", top: "22%",
          background: wallColor,
          borderRadius: "3px 3px 0 0",
          boxShadow: "inset -8px 0 16px rgba(0,0,0,0.08), inset 0 -4px 8px rgba(0,0,0,0.06)",
          transition: "background 0.5s ease",
          overflow: "hidden",
        }}>
          {/* Baseboard */}
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 10, background: "rgba(255,255,255,0.4)", borderTop: "1px solid rgba(255,255,255,0.5)" }} />
          {/* Crown molding */}
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 8, background: "rgba(255,255,255,0.4)", borderBottom: "1px solid rgba(255,255,255,0.5)" }} />
          {/* Window */}
          <div style={{ position: "absolute", top: "18%", left: "15%", width: "25%", height: "40%", background: "rgba(180,220,255,0.35)", border: "3px solid rgba(255,255,255,0.6)", borderRadius: 1 }}>
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 50%)" }} />
            <div style={{ position: "absolute", top: "48%", left: 0, right: 0, height: 2, background: "rgba(255,255,255,0.6)" }} />
            <div style={{ position: "absolute", left: "48%", top: 0, bottom: 0, width: 2, background: "rgba(255,255,255,0.6)" }} />
          </div>
          {/* Door */}
          <div style={{ position: "absolute", top: "20%", right: "12%", width: "18%", height: "60%", background: "rgba(0,0,0,0.08)", border: "2px solid rgba(255,255,255,0.5)", borderRadius: "1px 1px 0 0" }}>
            <div style={{ position: "absolute", top: "48%", left: 4, width: 4, height: 4, borderRadius: "50%", background: "rgba(200,160,80,0.7)" }} />
          </div>
          {/* Paint roller effect */}
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(255,255,255,0.04) 0%, transparent 40%, rgba(0,0,0,0.04) 100%)" }} />
        </div>
      ) : isDeckStain ? (
        /* Deck stain view */
        <div style={{
          position: "absolute", bottom: "12%", left: "12%", right: "12%", height: "28%",
          background: wallColor,
          borderRadius: "3px 3px 0 0",
          transform: "perspective(500px) rotateX(20deg)",
          transformOrigin: "bottom center",
          boxShadow: "0 -3px 12px rgba(0,0,0,0.15)",
          transition: "background 0.5s ease",
          overflow: "hidden",
        }}>
          {Array.from({ length: 14 }).map((_, i) => (
            <div key={`board-${i}`} style={{ position: "absolute", top: `${i * 7.14}%`, left: 0, right: 0, height: "1px", background: "rgba(0,0,0,0.1)" }} />
          ))}
        </div>
      ) : (
        /* Exterior house view */
        <>
          {/* House body */}
          <div style={{
            position: "absolute", bottom: "14%", left: "18%", right: "18%", height: "40%",
            background: wallColor,
            borderRadius: "0 0 2px 2px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
            transition: "background 0.5s ease",
            overflow: "hidden",
          }}>
            {/* Siding lines */}
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={`siding-${i}`} style={{ position: "absolute", top: `${i * 8.33}%`, left: 0, right: 0, height: "1px", background: "rgba(0,0,0,0.07)" }} />
            ))}
            {/* Windows */}
            <div style={{ position: "absolute", top: "20%", left: "12%", width: "22%", height: "35%", background: "rgba(180,215,255,0.4)", border: "3px solid rgba(255,255,255,0.7)", borderRadius: 1 }}>
              <div style={{ position: "absolute", top: "48%", left: 0, right: 0, height: 2, background: "rgba(255,255,255,0.6)" }} />
              <div style={{ position: "absolute", left: "48%", top: 0, bottom: 0, width: 2, background: "rgba(255,255,255,0.6)" }} />
            </div>
            <div style={{ position: "absolute", top: "20%", right: "12%", width: "22%", height: "35%", background: "rgba(180,215,255,0.4)", border: "3px solid rgba(255,255,255,0.7)", borderRadius: 1 }}>
              <div style={{ position: "absolute", top: "48%", left: 0, right: 0, height: 2, background: "rgba(255,255,255,0.6)" }} />
              <div style={{ position: "absolute", left: "48%", top: 0, bottom: 0, width: 2, background: "rgba(255,255,255,0.6)" }} />
            </div>
            {/* Door */}
            <div style={{ position: "absolute", bottom: 0, left: "38%", width: "24%", height: "55%", background: hasTrim ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.1)", border: hasTrim ? "2px solid rgba(255,255,255,0.7)" : "2px solid rgba(0,0,0,0.1)", borderRadius: "1px 1px 0 0" }}>
              <div style={{ position: "absolute", top: "48%", left: 4, width: 4, height: 4, borderRadius: "50%", background: "rgba(200,160,80,0.8)" }} />
            </div>
            {/* Trim highlight */}
            {hasTrim && <div style={{ position: "absolute", inset: 0, border: "5px solid rgba(255,255,255,0.25)", pointerEvents: "none" }} />}
          </div>

          {/* Roof */}
          <svg style={{ position: "absolute", bottom: "calc(14% + 40%)", left: "14%", width: "72%", height: 40 }} viewBox="0 0 100 40" preserveAspectRatio="none">
            <polygon points="50,0 0,40 100,40" fill="rgba(60,60,60,0.7)" />
          </svg>

          {/* Garage */}
          {hasGarage && (
            <div style={{ position: "absolute", bottom: "14%", left: "60%", width: "14%", height: "28%", background: wallColor, border: "2px solid rgba(0,0,0,0.12)", transition: "background 0.5s ease" }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={`gd-${i}`} style={{ position: "absolute", top: `${i * 20}%`, left: 0, right: 0, height: 1, background: "rgba(0,0,0,0.15)" }} />
              ))}
            </div>
          )}

          {/* Fence */}
          {hasFence && (
            <div style={{ position: "absolute", bottom: "14%", left: "8%", right: "8%", height: 18, display: "flex", alignItems: "flex-start", gap: 3 }}>
              {Array.from({ length: 24 }).map((_, i) => (
                <div key={`fence-${i}`} style={{ flex: 1, height: "100%", background: wallColor, borderRadius: "1px 1px 0 0", opacity: 0.85, boxShadow: "0 1px 2px rgba(0,0,0,0.12)" }} />
              ))}
            </div>
          )}
        </>
      )}

      {/* Labels */}
      <div style={{ position: "absolute", bottom: 8, left: 12, padding: "4px 10px", borderRadius: 4, background: "rgba(0,0,0,0.55)", color: "white", fontSize: 10, fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>
        {service.name}{color?.name ? ` · ${color.name}` : ""}
      </div>
      <div style={{ position: "absolute", bottom: 8, right: 12, padding: "4px 10px", borderRadius: 4, background: "rgba(0,0,0,0.55)", color: "white", fontSize: 10, fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>
        {sqft || 1200} sqft
      </div>
    </>
  );
}

// ─── ROOFING PREVIEW ───
function RoofingScene({ sqft, material, extras, color, region }) {
  const roofMat = material || { id: "arch-shingles-std", name: "Arch Shingles", swatch: "#6a6a6a" };
  const roofColor = color?.hex || roofMat.swatch || "#6a6a6a";
  const isMetal  = roofMat.id?.startsWith("metal");
  const isFlat   = roofMat.id === "flat-tpo";
  const isCedar  = roofMat.id === "cedar-shake";
  const hasRidge = extras?.some(e => e.id === "ridge-vent");
  const hasGutters = extras?.some(e => e.id === "gutters");
  const hasSkylight = extras?.some(e => e.id === "skylights");
  const hasIceWater = extras?.some(e => e.id === "ice-water");
  const footprint = Math.round(Math.sqrt(sqft || 1500) * 1.2);

  return (
    <>
      <BackgroundScene region={region} hasLighting={false} />

      {/* House walls */}
      <div style={{
        position: "absolute", bottom: "14%", left: "20%", right: "20%", height: "28%",
        background: "rgba(240,235,225,0.85)",
        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        overflow: "hidden",
      }}>
        {/* Siding */}
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={`siding-${i}`} style={{ position: "absolute", top: `${i * 12.5}%`, left: 0, right: 0, height: 1, background: "rgba(0,0,0,0.06)" }} />
        ))}
        {/* Windows */}
        <div style={{ position: "absolute", top: "20%", left: "10%", width: "20%", height: "40%", background: "rgba(180,215,255,0.4)", border: "2px solid rgba(200,200,200,0.6)", borderRadius: 1 }} />
        <div style={{ position: "absolute", top: "20%", right: "10%", width: "20%", height: "40%", background: "rgba(180,215,255,0.4)", border: "2px solid rgba(200,200,200,0.6)", borderRadius: 1 }} />
      </div>

      {/* Roof — gable style */}
      {!isFlat ? (
        <>
          {/* Left slope */}
          <div style={{
            position: "absolute",
            bottom: "calc(14% + 28%)",
            left: "16%",
            width: "35%",
            height: "22%",
            background: roofColor,
            transform: "skewX(-28deg)",
            transformOrigin: "bottom right",
            boxShadow: "-2px 2px 8px rgba(0,0,0,0.2)",
            transition: "background 0.5s ease",
            overflow: "hidden",
          }}>
            {/* Shingle rows — asphalt / cedar */}
            {!isMetal && Array.from({ length: 10 }).map((_, i) => (
              <div key={`shingle-l-${i}`} style={{ position: "absolute", top: `${i * 10}%`, left: 0, right: 0, height: "8%", background: i % 2 === 0 ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                {isCedar && Array.from({ length: 8 }).map((_, j) => (
                  <div key={`cedar-l-${i}-${j}`} style={{ display: "inline-block", width: "12%", height: "100%", borderRight: "1px solid rgba(0,0,0,0.1)", background: j % 2 === 0 ? "rgba(0,0,0,0.04)" : "transparent" }} />
                ))}
              </div>
            ))}
            {/* Metal seams */}
            {isMetal && Array.from({ length: 8 }).map((_, i) => (
              <div key={`seam-l-${i}`} style={{ position: "absolute", top: 0, bottom: 0, left: `${i * 12.5}%`, width: 2, background: "rgba(0,0,0,0.15)" }} />
            ))}
            {/* Ice & water shield highlight */}
            {hasIceWater && <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "15%", background: "rgba(100,150,200,0.15)", borderTop: "1px solid rgba(100,150,200,0.25)" }} />}
          </div>

          {/* Right slope */}
          <div style={{
            position: "absolute",
            bottom: "calc(14% + 28%)",
            right: "16%",
            width: "35%",
            height: "22%",
            background: roofColor,
            transform: "skewX(28deg)",
            transformOrigin: "bottom left",
            boxShadow: "2px 2px 8px rgba(0,0,0,0.2)",
            transition: "background 0.5s ease",
            overflow: "hidden",
          }}>
            {!isMetal && Array.from({ length: 10 }).map((_, i) => (
              <div key={`shingle-r-${i}`} style={{ position: "absolute", top: `${i * 10}%`, left: 0, right: 0, height: "8%", background: i % 2 === 0 ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.04)", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                {isCedar && Array.from({ length: 8 }).map((_, j) => (
                  <div key={`cedar-r-${i}-${j}`} style={{ display: "inline-block", width: "12%", height: "100%", borderRight: "1px solid rgba(0,0,0,0.1)", background: j % 2 === 0 ? "rgba(0,0,0,0.04)" : "transparent" }} />
                ))}
              </div>
            ))}
            {isMetal && Array.from({ length: 8 }).map((_, i) => (
              <div key={`seam-r-${i}`} style={{ position: "absolute", top: 0, bottom: 0, left: `${i * 12.5}%`, width: 2, background: "rgba(0,0,0,0.15)" }} />
            ))}
            {hasIceWater && <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "15%", background: "rgba(100,150,200,0.15)", borderTop: "1px solid rgba(100,150,200,0.25)" }} />}
          </div>

          {/* Ridge */}
          <div style={{
            position: "absolute",
            bottom: `calc(14% + 28% + 22% - 3px)`,
            left: "49.5%",
            width: "1%",
            height: 6,
            background: "rgba(0,0,0,0.3)",
          }} />
          {/* Ridge vent */}
          {hasRidge && (
            <div style={{
              position: "absolute",
              bottom: `calc(14% + 28% + 22%)`,
              left: "46%",
              width: "8%",
              height: 5,
              background: "rgba(80,80,80,0.4)",
              borderRadius: 2,
            }} />
          )}
        </>
      ) : (
        /* Flat/TPO roof */
        <div style={{
          position: "absolute",
          bottom: "calc(14% + 28%)",
          left: "18%",
          right: "18%",
          height: 8,
          background: roofColor,
          boxShadow: "0 -1px 4px rgba(0,0,0,0.15)",
          transition: "background 0.5s ease",
        }} />
      )}

      {/* Gutters */}
      {hasGutters && (
        <>
          <div style={{ position: "absolute", bottom: "calc(14% + 28%)", left: "16%", right: "16%", height: 5, background: "rgba(100,100,100,0.5)", borderRadius: "0 0 3px 3px" }} />
        </>
      )}

      {/* Skylight */}
      {hasSkylight && (
        <div style={{ position: "absolute", bottom: `calc(14% + 28% + 8%)`, left: "54%", width: "8%", height: "7%", background: "rgba(180,220,255,0.5)", border: "1px solid rgba(200,200,200,0.6)", transform: "skewX(28deg)" }} />
      )}

      {/* Labels */}
      <div style={{ position: "absolute", bottom: 8, left: 12, padding: "4px 10px", borderRadius: 4, background: "rgba(0,0,0,0.55)", color: "white", fontSize: 10, fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>
        {roofMat.name}{color?.name ? ` · ${color.name}` : ""} · {footprint}' × {footprint}'
      </div>
      <div style={{ position: "absolute", bottom: 8, right: 12, padding: "4px 10px", borderRadius: 4, background: "rgba(0,0,0,0.55)", color: "white", fontSize: 10, fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>
        {sqft || 1500} sqft
      </div>
    </>
  );
}

// ─── MAIN EXPORT ───
export default function TradePreview({ trade, market, sqft, material, railing, extras, color }) {
  const tradeSlug = trade?.slug || "decks";
  const region = getRegion(market?.state || "");

  const labelMap = {
    decks: "Deck Preview",
    concrete: "Concrete Preview",
    painting: "Painting Preview",
    roofing: "Roofing Preview",
  };

  return (
    <div style={{
      width: "100%", height: 220, borderRadius: 12, overflow: "hidden",
      position: "relative",
    }}>
      {tradeSlug === "decks" && (
        <DeckScene sqft={sqft} material={material} railing={railing} extras={extras} color={color} region={region} />
      )}
      {tradeSlug === "concrete" && (
        <ConcreteScene sqft={sqft} material={material} extras={extras} color={color} region={region} />
      )}
      {tradeSlug === "painting" && (
        <PaintingScene sqft={sqft} material={material} extras={extras} color={color} region={region} />
      )}
      {tradeSlug === "roofing" && (
        <RoofingScene sqft={sqft} material={material} extras={extras} color={color} region={region} />
      )}

      {/* UltraLife Trades watermark */}
      <div style={{
        position: "absolute", top: 10, left: 12,
        fontSize: 9, fontWeight: 700, letterSpacing: 1.5,
        color: "rgba(255,255,255,0.5)", fontFamily: "'DM Sans', sans-serif",
        textTransform: "uppercase",
      }}>
        {labelMap[tradeSlug] || "Preview"}
      </div>
    </div>
  );
}
